import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';

import {
  ApiError,
  classesApi,
  type AcademicLevel,
  type FieldIssue,
} from '../services/api/client';
import { fieldLabel } from '../services/api/fieldLabels';

/** Sugere o período letivo atual: "2026/1" no primeiro semestre, "2026/2" no segundo. */
function currentTerm(): string {
  const now = new Date();
  return `${now.getFullYear()}/${now.getMonth() < 6 ? 1 : 2}`;
}

export default function NewClassPage() {
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [discipline, setDiscipline] = useState('Gestão de Processos');
  const [course, setCourse] = useState('');
  const [level, setLevel] = useState<AcademicLevel>('UNDERGRADUATE');
  const [entrySemester, setEntrySemester] = useState('');
  const [disciplineSemester, setDisciplineSemester] = useState('1');
  const [academicTerm, setAcademicTerm] = useState(currentTerm());

  const [error, setError] = useState('');
  const [issues, setIssues] = useState<FieldIssue[]>([]);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError('');
    setIssues([]);
    setSubmitting(true);

    try {
      const result = await classesApi.create({
        name,
        discipline,
        course,
        level,
        entrySemester,
        disciplineSemester: Number(disciplineSemester),
        academicTerm,
      });

      void navigate(`/turmas/${result.class.id}`, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setIssues(err.issues ?? []);
      } else {
        setError('Não foi possível criar a turma.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <div className="section-head">
        <h2>Nova turma</h2>
      </div>

      <section className="card">
        <form onSubmit={handleSubmit} noValidate>
          <label htmlFor="name">Nome da turma</label>
          <input
            id="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex.: Administração 5º semestre — noturno"
          />

          <label htmlFor="discipline">Disciplina</label>
          <input
            id="discipline"
            required
            value={discipline}
            onChange={(e) => setDiscipline(e.target.value)}
          />

          <label htmlFor="course">Curso</label>
          <input
            id="course"
            required
            value={course}
            onChange={(e) => setCourse(e.target.value)}
            placeholder="Ex.: Administração"
          />

          <label htmlFor="level">Nível</label>
          <select
            id="level"
            value={level}
            onChange={(e) => setLevel(e.target.value as AcademicLevel)}
          >
            <option value="UNDERGRADUATE">Graduação</option>
            <option value="GRADUATE">Pós-graduação</option>
          </select>

          <div className="form-row">
            <div>
              <label htmlFor="entrySemester">Semestre de entrada da turma</label>
              <input
                id="entrySemester"
                required
                value={entrySemester}
                onChange={(e) => setEntrySemester(e.target.value)}
                placeholder="2025/1"
                pattern="\d{4}/[12]"
                title="Ano com quatro dígitos, barra, e 1 ou 2"
              />
              <p className="field-hint">
                Quando a turma entrou na faculdade. Ano/semestre — <strong>1</strong> ou{' '}
                <strong>2</strong> depois da barra.
              </p>
            </div>

            <div>
              <label htmlFor="disciplineSemester">Semestre da disciplina</label>
              <input
                id="disciplineSemester"
                type="number"
                min={1}
                max={20}
                required
                value={disciplineSemester}
                onChange={(e) => setDisciplineSemester(e.target.value)}
              />
              <p className="field-hint">Em qual semestre ela é cursada.</p>
            </div>
          </div>

          <label htmlFor="academicTerm">Período letivo</label>
          <input
            id="academicTerm"
            required
            value={academicTerm}
            onChange={(e) => setAcademicTerm(e.target.value)}
            placeholder="2026/2"
            pattern="\d{4}/[12]"
            title="Ano com quatro dígitos, barra, e 1 ou 2"
          />
          <p className="field-hint">
            Semestre em que a turma está cursando a disciplina agora.
          </p>

          {/* Quando há erros de campo, a mensagem genérica "Dados inválidos"
              vira ruído: ela repete o óbvio e empurra para baixo o que
              realmente diz o que corrigir. */}
          {error && issues.length === 0 && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}

          {issues.length > 0 && (
            <ul className="form-issues" role="alert">
              {issues.map((issue) => (
                <li key={issue.field}>
                  <strong>{fieldLabel(issue.field)}</strong>: {issue.message}
                </li>
              ))}
            </ul>
          )}

          <div className="form-actions">
            <button type="button" onClick={() => void navigate('/turmas')}>
              Cancelar
            </button>
            <button type="submit" className="primary" disabled={submitting}>
              {submitting ? 'Criando…' : 'Criar turma'}
            </button>
          </div>
        </form>
      </section>
    </>
  );
}
